import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Sparkles, ArrowRight, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { PRICING_PLANS, EXTRA_OPTIONS } from '../mock';

export const Pricing = () => {
  const [selectedBooth, setSelectedBooth] = useState('classique');

  const filteredPlans = PRICING_PLANS.filter(plan => plan.availableFor.includes(selectedBooth));

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero with Visual Background */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div 
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1545873509-33e944ca7655?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-captiva-cream/95 to-captiva-cream-dark/95 -z-10" />
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center space-x-2 bg-captiva-cream-dark text-captiva-beige-dark px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4 text-captiva-gold" />
            <span>Tarifs transparents sans surprise</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Nos Tarifs
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Des formules adaptées à tous les budgets • Installation incluse • Photos illimitées
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center space-x-4 mb-12">
            <Button
              onClick={() => setSelectedBooth('classique')}
              variant={selectedBooth === 'classique' ? 'default' : 'outline'}
              size="lg"
              className={selectedBooth === 'classique' ? 'bg-captiva-gold hover:bg-captiva-gold-dark text-white' : 'border-captiva-gold text-captiva-gold hover:bg-captiva-cream'}
            >
              Photobooth Classique
            </Button>
            <Button
              onClick={() => setSelectedBooth('miroir')}
              variant={selectedBooth === 'miroir' ? 'default' : 'outline'}
              size="lg"
              className={selectedBooth === 'miroir' ? 'bg-captiva-gold hover:bg-captiva-gold-dark text-white' : 'border-captiva-gold text-captiva-gold hover:bg-captiva-cream'}
            >
              Miroir Booth
            </Button>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  plan.popular 
                    ? 'border-4 border-captiva-gold shadow-2xl scale-[1.02]' 
                    : 'border-2 border-gray-200 hover:border-captiva-gold-light hover:shadow-xl'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-captiva-gold text-white px-4 py-2 text-xs font-bold rounded-bl-lg">
                    Notre best seller
                  </div>
                )}
                
                <div className="p-6">
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                  {plan.subtitle && (
                    <p className="text-sm text-captiva-gold mb-4">{plan.subtitle}</p>
                  )}
                  
                  {/* Price */}
                  <div className="mb-6">
                    <p className="text-5xl font-bold text-captiva-gold">{plan.price}€</p>
                    <p className="text-sm text-gray-500">TTC</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-captiva-gold flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className={`w-full ${
                        plan.popular
                          ? 'bg-captiva-gold hover:bg-captiva-gold-dark text-white shadow-lg'
                          : 'bg-captiva-cream hover:bg-captiva-cream-dark text-captiva-beige-dark border-2 border-captiva-gold-light'
                      }`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Extra Options */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white to-captiva-cream/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Options supplémentaires
            </h2>
            <p className="text-xl text-gray-600">
              Personnalisez votre expérience avec nos services premium
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {EXTRA_OPTIONS.map((option) => (
              <Card key={option.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-captiva-gold-light group">
                <div className="grid md:grid-cols-5 gap-0">
                  {/* Image */}
                  <div className="md:col-span-2 relative h-64 md:h-auto overflow-hidden bg-gray-100">
                    <img
                      src={option.image}
                      alt={option.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="md:col-span-3 p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{option.name}</h3>
                      <div className={`flex items-center space-x-1 ${option.price === 'Offert' ? 'text-green-600' : 'text-captiva-gold'}`}>
                        {option.price !== 'Offert' && option.price !== 'Sur devis' && <Plus className="h-5 w-5" />}
                        <span className="text-2xl font-bold">
                          {option.price === 'Offert' ? 'Offert' : (typeof option.price === 'number' ? `${option.price}€` : option.price)}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-captiva-gold font-medium mb-3">{option.description}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{option.details}</p>
                    
                    <Link to="/contact" className="mt-4 inline-block">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-captiva-gold text-captiva-gold hover:bg-captiva-cream"
                      >
                        Ajouter à ma réservation
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Questions fréquentes
            </h2>
          </div>

          <div className="space-y-4">
            {/* Installation & démontage */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                📦 Devons-nous installer le photobooth nous-mêmes ?
              </h3>
              <p className="text-gray-600">
                Non, nous nous chargeons entièrement du montage et du démontage du photobooth ou miroirbooth. Vous n'avez rien à faire !
              </p>
            </Card>

            {/* Livraison */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🚚 La livraison est-elle comprise dans le prix ?
              </h3>
              <p className="text-gray-600">
                Oui, la livraison est gratuite jusqu'à 20 km autour de notre point de départ. Au-delà, un supplément de 1 €/km sera appliqué.
              </p>
            </Card>

            {/* Tarification */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                💶 Les prix affichés sont-ils TVA comprise ?
              </h3>
              <p className="text-gray-600">
                Oui, tous nos tarifs sont TVAC (toutes taxes comprises).
              </p>
            </Card>

            {/* Assistance */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                📞 Que faire en cas de problème avec le photobooth pendant la soirée ?
              </h3>
              <p className="text-gray-600">
                Pas d'inquiétude, nous restons joignables jusqu'à 23h00 le jour de votre événement pour toute assistance technique.
              </p>
            </Card>

            {/* Récupération des photos */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                📸 Comment et quand recevons-nous les photos ?
              </h3>
              <p className="text-gray-600">
                Vous recevrez l'ensemble des photos le lendemain ou surlendemain de l'événement, via un lien de téléchargement sécurisé.
              </p>
            </Card>

            {/* Formats disponibles */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🖼️ Quels sont les formats d'impression proposés ?
              </h3>
              <p className="text-gray-600">
                Nous proposons deux formats de tirages :<br/>
                • 5x15 cm (bande photomaton)<br/>
                • 10x15 cm (photo classique)
              </p>
            </Card>

            {/* Installation technique */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                ⚡ Quelles sont les conditions pour l'installation du photobooth ?
              </h3>
              <p className="text-gray-600">
                Le photobooth doit être placé à proximité d'une prise électrique. Aucun éclairage supplémentaire n'est nécessaire : il est équipé d'un éclairage LED intégré.
              </p>
            </Card>

            {/* Connexion internet */}
            <Card className="p-6 hover:shadow-lg transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🌐 Que se passe-t-il si le lieu n'a pas de Wi-Fi ?
              </h3>
              <p className="text-gray-600">
                Aucun souci ! Les envois par e-mail et WhatsApp seront effectués ultérieurement, en même temps que l'envoi du lien contenant toutes les photos.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-captiva-beige to-captiva-gold-dark text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à réserver votre photobooth ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Contactez-nous pour un devis personnalisé ou réservez directement en ligne
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-captiva-gold-dark hover:bg-gray-100 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Demander un devis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-captiva-gold-dark px-8 py-6 text-lg transition-all duration-300"
              >
                Réserver maintenant
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
